import { Locale } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import { LOCAL_STORAGE } from '@/constants';
import { logger } from '@/services/logger';

declare const window: {
    dateFns: {
        locale: Record<string, Locale>;
    };
};

const defaultLocale = 'en-US';

const toDateFnsLocale = (locale: string) => locale.replace(/_/g, '-');

/**
 * Get the current app language from localStorage or HTML lang attribute
 */
const getAppLanguage = (): string => {
    const storedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    if (storedLanguage) return toDateFnsLocale(storedLanguage);

    const htmlTag = document.getElementsByTagName('html')[0] as HTMLElement;
    const fullLanguageKey = htmlTag.getAttribute('lang') as string;
    return fullLanguageKey ? toDateFnsLocale(fullLanguageKey.slice(0, 2)) : defaultLocale;
};

const useFormattedDate = () => {
    const [isLocaleLoaded, setIsLocaleLoaded] = useState(false);
    const [currentLocale, setCurrentLocale] = useState<string>(getAppLanguage());

    const loadLocale = useCallback(async (locale: string, requestedLocale?: string) => {
        setIsLocaleLoaded(false);
        const resolved = requestedLocale ?? locale;

        const existingScript = document.querySelector(`script[src*="date-fns@latest/locale/${locale}"]`);
        if (existingScript) existingScript.remove();

        const script = document.createElement('script');
        script.src = `https://cdn.jsdelivr.net/npm/date-fns@latest/locale/${locale}/cdn.js`;

        script.onload = () => {
            if (window.dateFns?.locale?.[locale]) {
                registerLocale(locale, window.dateFns.locale[locale]);
                setDefaultLocale(locale);
                setCurrentLocale(resolved);
                setIsLocaleLoaded(true);
                logger.debug(`Successfully loaded date-fns locale: ${locale}`);
            } else {
                logger.warn(`Failed to register locale ${locale}, falling back to ${defaultLocale}`);
                if (window.dateFns?.locale?.[defaultLocale]) {
                    registerLocale(defaultLocale, window.dateFns.locale[defaultLocale]);
                    setDefaultLocale(defaultLocale);
                    setCurrentLocale(resolved);
                }
                setIsLocaleLoaded(true);
            }
        };

        script.onerror = () => {
            const base = locale.split('-')[0];
            if (base && base !== locale) {
                loadLocale(base, resolved);
            } else {
                logger.warn(`Error loading locale ${locale}: using default ${defaultLocale}`);
                if (window.dateFns?.locale?.[defaultLocale]) {
                    registerLocale(defaultLocale, window.dateFns.locale[defaultLocale]);
                    setDefaultLocale(defaultLocale);
                    setCurrentLocale(resolved);
                }
                setIsLocaleLoaded(true);
            }
        };

        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        const initialLocale = getAppLanguage();
        loadLocale(initialLocale);

        // Watch for language changes by observing localStorage changes
        const handleLanguageChange = () => {
            const newLanguage = getAppLanguage();
            if (newLanguage !== currentLocale) {
                logger.debug(`Language changed from ${currentLocale} to ${newLanguage}, reloading date-fns locale`);
                loadLocale(newLanguage);
            }
        };

        // Listen for storage events (when localStorage changes)
        globalThis.addEventListener('storage', handleLanguageChange);

        // Also watch for HTML lang attribute changes (shell app language changes)
        const observer = new MutationObserver(() => {
            const newLanguage = getAppLanguage();
            if (newLanguage !== currentLocale) {
                logger.debug(`HTML lang changed, reloading date-fns locale from ${currentLocale} to ${newLanguage}`);
                loadLocale(newLanguage);
            }
        });

        const htmlNode = document.getElementsByTagName('html')[0] as HTMLElement;
        observer.observe(htmlNode, { attributes: true, attributeFilter: ['lang'] });

        return () => {
            globalThis.removeEventListener('storage', handleLanguageChange);
            observer.disconnect();
        };
    }, [loadLocale, currentLocale]);

    return { isLocaleLoaded, currentLocale };
};
export { useFormattedDate };
